/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-classic-components, ember/no-component-lifecycle-hooks, ember/no-computed-properties-in-native-classes, ember/no-get, ember/require-tagless-components, warp-drive/no-legacy-request-patterns */
import { tracked } from '@glimmer/tracking';
import Component from '@ember/component';
// Ember imports
import { service } from '@ember/service';
import { action, computed } from '@ember/object';
// Internal imports
import { optionAlias } from '@linc-technologies/linced-common/utils/options';
import Scopes from '@linc-technologies/linced-common/enums/scopes';
import moment from 'moment';

export default class BottomNavigation extends Component {
  @action
  setGroupSelectorYear(value) {
    this.groupSelectorYear = value;
  }

  @action
  setQuickSearchText(value) {
    this.quickSearchText = value;
  }

  // Services
  @service
  features;

  @service
  groupYears;

  @service
  options;

  @service
  permissions;

  // NOTE: injected under a different local name - the `quickSearch` action
  // below is a sibling class member and would otherwise shadow the service.
  @service('quick-search')
  quickSearchService;

  @service
  router;

  @service
  session;

  @service
  store;

  @service
  strings;

  @service
  students;

  @tracked peopleSelectorDialogOpen = false;
  @tracked groupSelectorDialogOpen = false;
  @tracked menuOpen = false;
  @tracked
  quickSearchDialogOpen = false;
  @tracked
  quickSearchText = '';

  didReceiveAttrs() {
    super.didReceiveAttrs(...arguments);
    // Close the menu overlay and dialogs whenever the route changes
    if (this._lastURL && this._lastURL !== this.url) {
      this.menuOpen = false;
      this.groupSelectorDialogOpen = false;
      this.peopleSelectorDialogOpen = false;
    }
    this._lastURL = this.url;
  }

  @computed('peopleSelectorDialogOpen', 'students.all.[]')
  get people() {
    // Trigger students service to load all students if not already primed.
    // This property will then recalculate once they're loaded.
    this.get('students.all');
    return this.store.peekAll('person');
  }

  @tracked groupSelectorYear = moment().year();
  @tracked currentYear = moment().year();

  get groups() {
    if (!this.groupSelectorDialogOpen) {
      // Early return so we don't risk fetching data when closed.
      return [];
    }

    return this.groupYears.groupsFor(this.groupSelectorYear, this.currentYear);
  }

  get groupSelectorClasses() {
    let classes = ['group-selector-dialog'];
    if (this.groupSelectorYear !== this.currentYear) {
      classes.push('non-current-year');
    }

    return classes.join(' ');
  }

  @optionAlias('core:staffPermissions:hideNextYearsPlacementsForTeachers')
  hideNextYearsPlacementsForTeachers;

  @computed('session.scopes.[]')
  get hasAdminAccess() {
    return this.permissions.meet(Scopes.TENANT_ADMIN);
  }

  @computed(
    'hasAdminAccess',
    'hideNextYearsPlacementsForTeachers',
    'groupSelectorYear',
    'currentYear',
  )
  get disableNextYear() {
    if (this.groupSelectorYear === this.currentYear + 1) {
      return true;
    }
    if (this.groupSelectorYear === this.currentYear) {
      return !this.hasAdminAccess && this.hideNextYearsPlacementsForTeachers;
    }
    return false;
  }

  // isActive attributes. Needed as buttons are now filled in whenever a menu item is active
  @computed(
    'router.currentRouteName',
    'peopleSelectorDialogOpen',
    'groupSelectorDialogOpen',
    'menuOpen',
  )
  get isHomeActive() {
    if (
      this.peopleSelectorDialogOpen ||
      this.groupSelectorDialogOpen ||
      this.menuOpen
    )
      return false;
    let route = this.router.currentRouteName || '';
    return route.startsWith('home') && !route.startsWith('home.events');
  }

  get isStudentsActive() {
    return this.peopleSelectorDialogOpen;
  }

  get isGroupsActive() {
    return this.groupSelectorDialogOpen;
  }

  @computed(
    'router.currentRouteName',
    'peopleSelectorDialogOpen',
    'groupSelectorDialogOpen',
    'menuOpen',
  )
  get isEventsActive() {
    if (
      this.peopleSelectorDialogOpen ||
      this.groupSelectorDialogOpen ||
      this.menuOpen
    )
      return false;
    return (this.router.currentRouteName || '').startsWith('home.events');
  }

  @computed(
    'router.currentRouteName',
    'peopleSelectorDialogOpen',
    'groupSelectorDialogOpen',
    'menuOpen',
  )
  get isSearchActive() {
    if (
      this.peopleSelectorDialogOpen ||
      this.groupSelectorDialogOpen ||
      this.menuOpen
    )
      return false;
    return (this.router.currentRouteName || '').startsWith('search');
  }

  @computed(
    'router.currentRouteName',
    'peopleSelectorDialogOpen',
    'groupSelectorDialogOpen',
    'menuOpen',
  )
  get isHeroActive() {
    if (
      this.peopleSelectorDialogOpen ||
      this.groupSelectorDialogOpen ||
      this.menuOpen
    )
      return false;
    return this.router.currentRouteName === 'about';
  }

  @action
  togglePeopleSelectorDialog() {
    this.menuOpen = false;
    this.toggleProperty('peopleSelectorDialogOpen');
  }

  @action
  onGroupCardClose() {
    this.groupSelectorDialogOpen = false;
  }

  @action
  toggleGroupSelectorDialog() {
    this.menuOpen = false;
    this.toggleProperty('groupSelectorDialogOpen');

    if (this.groupSelectorDialogOpen) {
      let thisYear = moment().year();
      // Update currentYear in case we've rolled over while the app's open.
      this.currentYear = thisYear;
      // Default back to current year when opened.
      this.groupSelectorYear = thisYear;
    }
  }

  @action
  toggleMenu() {
    this.toggleProperty('menuOpen');
  }

  @action
  closeMenu() {
    this.menuOpen = false;
  }

  @action
  toggleQuickSearchDialog() {
    this.menuOpen = false;
    this.toggleProperty('quickSearchDialogOpen');

    if (!this.quickSearchDialogOpen) {
      this.quickSearchText = '';
    }
  }

  @action
  quickSearch() {
    if (this.quickSearchText) {
      let searchText = this.quickSearchText;
      this.quickSearchDialogOpen = false;
      this.quickSearchText = '';

      this.quickSearchService.search(searchText);
    }
  }
}
